/**
 * ShareModal - 分享弹窗组件
 *
 * 用于引导用户分享到社交媒体以获取额外抽奖机会
 *
 * @module ShareModal
 * @date 2026-01-07
 */

'use client'

import React from 'react'
import { cn } from '../../helpers/index.js'
import { Heading } from '../../components/index.js'
import { BaseModal } from './BaseModal.js'

export type SocialPlatform = 'facebook' | 'twitter' | 'instagram' | 'linkedin' | 'tiktok' | 'youtube'

export interface SharePlatformConfig {
  /**
   * 平台类型
   */
  platform: SocialPlatform

  /**
   * 平台名称
   */
  name: string

  /**
   * 分享 URL（可选，如果提供则直接跳转）
   */
  url?: string

  /**
   * 点击回调（如果提供 url 则此回调会在跳转前执行）
   */
  onClick?: () => void

  /**
   * 是否禁用
   */
  disabled?: boolean
}

export interface ShareModalProps {
  /**
   * 是否显示弹窗
   */
  isOpen: boolean

  /**
   * 关闭弹窗回调
   */
  onClose: () => void

  /**
   * 弹窗标题
   * @default "Share to Get More Chances"
   */
  title?: string

  /**
   * 副标题/引导文案
   * @default "Share to Play Again Keep Trying! You Could Still Win Big!"
   */
  subtitle?: string

  /**
   * 底部提示文案
   * @default "Note: Maximum of 3 times per participant."
   */
  note?: string

  /**
   * 社交平台配置列表
   */
  platforms?: SharePlatformConfig[]

  /**
   * 分享成功回调
   */
  onShareSuccess?: (platform: SocialPlatform) => void

  /**
   * 自定义类名
   */
  className?: string

  /**
   * 主题模式
   * @default 'light'
   */
  theme?: 'light' | 'dark'
}

// 默认社交平台配置
const DEFAULT_PLATFORMS: SharePlatformConfig[] = [
  { platform: 'facebook', name: 'Facebook' },
  { platform: 'twitter', name: 'X (Twitter)' },
  { platform: 'instagram', name: 'Instagram' },
  { platform: 'linkedin', name: 'LinkedIn' },
  { platform: 'tiktok', name: 'TikTok' },
  { platform: 'youtube', name: 'YouTube' },
]

// 社交平台图标组件
const SocialIcon: React.FC<{ platform: SocialPlatform; className?: string }> = ({ platform, className }) => {
  const iconClass = cn('size-full', className)

  switch (platform) {
    case 'facebook':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
          <path
            d="M36 18C36 8.05887 27.9411 0 18 0C8.05887 0 0 8.05887 0 18C0 26.9842 6.5823 34.431 15.1875 35.7813V23.2031H10.6172V18H15.1875V14.0344C15.1875 9.52313 17.8748 7.03125 21.9864 7.03125C23.9551 7.03125 26.0156 7.38281 26.0156 7.38281V11.8125H23.7459C21.51 11.8125 20.8125 13.2001 20.8125 14.625V18H25.8047L25.0066 23.2031H20.8125V35.7813C29.4177 34.431 36 26.9842 36 18Z"
            fill="white"
          />
        </svg>
      )
    case 'twitter':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="33" height="30" viewBox="0 0 33 30" fill="none">
          <path
            d="M25.485 0H30.447L19.6065 12.39L32.3595 29.25H22.374L14.553 19.0245L5.604 29.25H0.639L12.234 15.9975L0 0H10.239L17.3085 9.3465L25.485 0ZM23.7435 26.28H26.493L8.745 2.814H5.7945L23.7435 26.28Z"
            fill="white"
          />
        </svg>
      )
    case 'instagram':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="33" viewBox="0 0 32 33" fill="none">
          <path
            d="M16.0088 0C20.3547 0 20.8991 0.0183266 22.6123 0.0996094C24.3132 0.174649 25.4768 0.449798 26.4961 0.84375C27.5467 1.25023 28.4411 1.80146 29.3291 2.68945C30.2231 3.5773 30.7672 4.47129 31.1611 5.53418C31.5551 6.54724 31.8302 7.71702 31.9053 9.41797C31.9803 11.125 31.999 11.6696 31.999 16.0156C31.999 20.3611 31.9803 20.9054 31.9053 22.6123C31.8302 24.3133 31.5551 25.4768 31.1611 26.4961C30.7547 27.5467 30.2044 28.4411 29.3164 29.3291C28.4284 30.217 27.5339 30.7674 26.4834 31.1738C25.4704 31.5678 24.3005 31.8429 22.5996 31.918C20.8928 31.993 20.3484 32.0117 16.0029 32.0117C11.6568 32.0117 11.1124 31.993 9.40527 31.918C7.70433 31.8429 6.5408 31.5678 5.52148 31.1738C4.47114 30.7674 3.57727 30.2168 2.68945 29.3291C1.79531 28.4412 1.2506 27.5469 0.837891 26.4902C0.443929 25.4772 0.168798 24.3073 0.09375 22.6064C0.0187082 20.8992 0 20.355 0 16.0088C3.73243e-07 11.6628 0.0187102 11.1187 0.09375 9.40527C0.168792 7.70433 0.443921 6.5408 0.837891 5.52148C1.25061 4.47102 1.79528 3.57737 2.68945 2.68945C3.57745 1.7952 4.47148 1.25062 5.52832 0.837891C6.54126 0.443997 7.71051 0.168809 9.41113 0.09375C11.1183 0.0187089 11.6627 3.7324e-07 16.0088 0ZM16.0088 2.88281C11.7317 2.88281 11.225 2.90153 9.54297 2.97656C7.98585 3.04535 7.13508 3.30847 6.57227 3.52734C5.82828 3.81497 5.29709 4.15899 4.73438 4.72168C4.17785 5.2782 3.82671 5.81549 3.53906 6.55957C3.32019 7.12238 3.05805 7.96693 2.98926 9.53027C2.91422 11.2124 2.89551 11.719 2.89551 15.9961C2.89551 20.2672 2.91422 20.7745 2.98926 22.4629C3.05806 24.0194 3.32024 24.8698 3.53906 25.4326C3.82672 26.1768 4.17156 26.7087 4.73438 27.2715C5.29081 27.8279 5.82829 28.1782 6.57227 28.4658C7.13508 28.6847 7.9796 28.9478 9.54297 29.0166C11.225 29.0916 11.7317 29.1104 16.0088 29.1104C20.2796 29.1104 20.7865 29.0916 22.4746 29.0166C24.0316 28.9478 24.8825 28.6847 25.4453 28.4658C26.1895 28.1782 26.7214 27.8343 27.2842 27.2715C27.8407 26.715 28.1909 26.1767 28.4785 25.4326C28.6973 24.8699 28.9595 24.0257 29.0283 22.4629C29.1034 20.7807 29.1221 20.2735 29.1221 15.9961C29.1221 11.7253 29.1034 11.2187 29.0283 9.53027C28.9595 7.97319 28.6974 7.12238 28.4785 6.55957C28.1909 5.81565 27.8468 5.28433 27.2842 4.72168C26.7276 4.16513 26.1895 3.815 25.4453 3.52734C24.8825 3.30847 24.038 3.04535 22.4746 2.97656C20.7928 2.90154 20.2858 2.88281 16.0088 2.88281ZM16.0127 7.78711C20.5526 7.78727 24.2354 11.4708 24.2354 16.0107C24.2352 20.5505 20.5525 24.2332 16.0127 24.2334C11.4728 24.2334 7.78923 20.5506 7.78906 16.0107C7.78906 11.4707 11.4727 7.78711 16.0127 7.78711ZM16.0127 10.6758C13.0673 10.6758 10.6777 13.0654 10.6777 16.0107C10.6779 18.956 13.0674 21.3447 16.0127 21.3447C18.9578 21.3446 21.3465 18.9559 21.3467 16.0107C21.3467 13.0655 18.9579 10.6759 16.0127 10.6758ZM24.5566 5.54102C25.6134 5.54107 26.4766 6.40413 26.4766 7.46094C26.4765 8.52395 25.6134 9.3808 24.5566 9.38086C23.4936 9.38086 22.6368 8.51773 22.6367 7.46094C22.6367 6.39784 23.4998 5.54102 24.5566 5.54102Z"
            fill="white"
          />
        </svg>
      )
    case 'linkedin':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" viewBox="0 0 29 29" fill="none">
          <path
            d="M26.6846 0H2.12757C0.951214 0 0 0.9287 0 2.07691V26.7353C0 27.8835 0.951214 28.8178 2.12757 28.8178H26.6846C27.861 28.8178 28.8178 27.8835 28.8178 26.7409V2.07691C28.8178 0.9287 27.861 0 26.6846 0ZM8.54967 24.5571H4.27202V10.8011H8.54967V24.5571ZM6.41084 8.92677C5.03749 8.92677 3.92868 7.81796 3.92868 6.45024C3.92868 5.08252 5.03749 3.97371 6.41084 3.97371C7.77856 3.97371 8.88737 5.08252 8.88737 6.45024C8.88737 7.81233 7.77856 8.92677 6.41084 8.92677ZM24.5571 24.5571H20.2851V17.8704C20.2851 16.2776 20.2569 14.2232 18.0618 14.2232C15.8386 14.2232 15.5008 15.9624 15.5008 17.7579V24.5571H11.2345V10.8011H15.332V12.681H15.3883C15.9567 11.6003 17.3526 10.4577 19.4295 10.4577C23.7578 10.4577 24.5571 13.3057 24.5571 17.0093V24.5571Z"
            fill="white"
          />
        </svg>
      )
    case 'tiktok':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path
            d="M32 4.51848C32 2.02299 29.977 0 27.4816 0H4.51848C2.02299 0 0 2.02299 0 4.51848V27.4816C0 29.9771 2.02299 32 4.51848 32H27.4816C29.977 32 32 29.9771 32 27.4816V4.51848ZM26.5102 13.9566C24.4458 13.9797 22.5219 13.3555 20.8328 12.1996V20.3146C20.8328 23.9211 18.364 27.0194 14.873 27.8285C10.1103 28.915 6.07845 25.4702 5.56992 21.355C5.03818 17.2397 7.66278 13.633 11.6856 12.8238C12.4716 12.662 13.654 12.662 14.2639 12.8007V17.1472C14.0762 17.1011 13.9005 17.0547 13.7156 17.0317C12.1434 16.7541 10.6207 17.5402 9.99648 18.9736C9.37229 20.4072 9.79008 22.0717 11.0616 23.0429C12.1251 23.875 13.3051 23.9906 14.5073 23.4357C15.7095 22.904 16.3557 21.9331 16.4942 20.6152C16.5174 20.4304 16.5162 20.2222 16.5162 20.0141V4.40826C16.5162 3.96899 16.5195 3.98827 16.9589 3.98827H20.4037C20.6581 3.98827 20.7506 4.02477 20.7736 4.32549C20.9586 7.00734 22.9947 9.29136 25.6072 9.63819C25.8846 9.68442 26.1819 9.7027 26.5104 9.72579L26.5102 13.9566Z"
            fill="white"
          />
        </svg>
      )
    case 'youtube':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="33" height="24" viewBox="0 0 33 24" fill="none">
          <path
            d="M28.5006 0.731224L26.3106 0.506224C19.7579 -0.168741 13.1533 -0.168741 6.60059 0.506224L4.50059 0.731224C3.24922 0.872661 2.09527 1.47475 1.26344 2.42027C0.431611 3.36578 -0.0185587 4.58703 0.000586503 5.84622V18.1462C-0.0185587 19.4054 0.431611 20.6267 1.26344 21.5722C2.09527 22.5177 3.24922 23.1198 4.50059 23.2612L6.69059 23.4862C13.2433 24.1612 19.8479 24.1612 26.4006 23.4862L28.5006 23.2612C29.752 23.1198 30.9059 22.5177 31.7377 21.5722C32.5696 20.6267 33.0197 19.4054 33.0006 18.1462V5.84622C33.0197 4.58703 32.5696 3.36578 31.7377 2.42027C30.9059 1.47475 29.752 0.872661 28.5006 0.731224ZM21.0006 12.9262L15.1656 16.8262C14.9985 16.918 14.8107 16.9658 14.6201 16.9651C14.4294 16.9644 14.242 16.9152 14.0756 16.8222C13.9091 16.7293 13.769 16.5955 13.6684 16.4335C13.5679 16.2715 13.5101 16.0867 13.5006 15.8962V8.09622C13.501 7.89217 13.5568 7.69205 13.6622 7.51731C13.7676 7.34256 13.9185 7.19977 14.0988 7.10422C14.2791 7.00868 14.482 6.96397 14.6858 6.97489C14.8896 6.98581 15.0865 7.05195 15.2556 7.16622L21.0906 11.0662C21.2453 11.1671 21.3725 11.3049 21.4605 11.4673C21.5485 11.6297 21.5946 11.8115 21.5946 11.9962C21.5946 12.1809 21.5485 12.3627 21.4605 12.5251C21.3725 12.6875 21.2453 12.8254 21.0906 12.9262H21.0006Z"
            fill="white"
          />
        </svg>
      )
    default:
      return null
  }
}

/**
 * ShareModal 分享弹窗组件
 */
export const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  title = 'Share to Get More Chances',
  subtitle = 'Share to Play Again Keep Trying! You Could Still Win Big!',
  note = 'Note: Maximum of 3 times per participant.',
  platforms = DEFAULT_PLATFORMS,
  onShareSuccess,
  className,
  theme = 'light',
}) => {
  const handlePlatformClick = (platformConfig: SharePlatformConfig) => {
    if (platformConfig.disabled) return

    // 如果提供了 onClick，执行自定义回调
    if (platformConfig.onClick) {
      platformConfig.onClick()
    }
    // 否则，如果提供了 URL，则打开新窗口
    else if (platformConfig.url) {
      window.open(platformConfig.url, '_blank', 'noopener,noreferrer')
    }

    // 触发成功回调
    onShareSuccess?.(platformConfig.platform)
  }

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-[326px] laptop:max-w-[420px]"
      background="bg-white"
      className={className}
      ariaLabelledBy="share-modal-title"
      theme={theme}
    >
      <div className="laptop:px-8 laptop:pb-8 p-6 pt-0 text-center">
        {/* 标题 */}
        <Heading size={2} id="share-modal-title" className="text-center tracking-[-0.32px] text-[#1D1D1F]">
          {title}
        </Heading>

        {/* 副标题 */}
        {subtitle && <p className="laptop:text-[16px] text-center text-[14px] font-[700] text-[#1D1D1F]">{subtitle}</p>}

        {/* 社交平台图标网格 */}
        <div className="mx-auto mt-8 flex max-w-[300px] flex-wrap items-center justify-center gap-4">
          {platforms.map(platformConfig => (
            <button
              key={platformConfig.platform}
              onClick={() => handlePlatformClick(platformConfig)}
              disabled={platformConfig.disabled}
              className={cn(
                'flex aspect-square size-[64px] items-center justify-center rounded-full bg-black text-white transition-all',
                'hover:scale-110 hover:shadow-lg',
                'focus:ring-brand-0 focus:outline-none focus:ring-2 focus:ring-offset-2',
                'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100'
              )}
              aria-label={`Share on ${platformConfig.name}`}
            >
              <SocialIcon platform={platformConfig.platform} />
            </button>
          ))}
        </div>

        {/* 底部提示 */}
        {note && <p className={cn('mt-8 text-[16px] font-[700] text-[#767880]', 'laptop:text-[16px]')}>{note}</p>}
      </div>
    </BaseModal>
  )
}

export default ShareModal
